import React, { useState, useEffect } from "react";
import { MakeApiCall } from "../../../../apis";
import { Upload, message, Input, Button } from "antd";
import { Wrapper } from "./style";
import { Helmet } from "react-helmet";
import { useCanEdit } from "../../../../contexts/SubUserAccessContext";

const MyProfile = ({ setData, userData }) => {
    const Id = userData?.user?.user_data?.id;
    const canEdit = useCanEdit();

    // --- Profile States ---
    const [isEditing, setIsEditing] = useState(false);
    const [profile, setProfile] = useState(null);
    const [editedProfile, setEditedProfile] = useState(null);
    const [imageChanged, setImageChanged] = useState(false);
    const [newImageFile, setNewImageFile] = useState(null);
    const [savingProfile, setSavingProfile] = useState(false);

    // --- Password States ---
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        old_password: "",
        new_password: "",
        confirm_password: ""
    });
    const [loadingPassword, setLoadingPassword] = useState(false);

    useEffect(() => {
        const getProfileDetails = async () => {
            const response = await MakeApiCall(`user/${Id}`, "get", null, true, {}, userData?.token);
            if (response?.status) {
                setProfile(response?.data || {});
            } else {
                message.warning(response?.message);
            }
        };
        if (Id) getProfileDetails();
    }, [Id, userData?.token]);

    // --- Profile Handlers ---
    const beforeUpload = (file) => {
        const isImage = file.type.startsWith("image/");
        if (!isImage) {
            message.error("You can only upload image files!");
            return Upload.LIST_IGNORE;
        }
        return false;
    };

    const handleProfilePictureChange = ({ file }) => {
        const rawFile = file.originFileObj || file;
        if (rawFile) {
            setNewImageFile(rawFile);
            setImageChanged(true);
            const previewURL = URL.createObjectURL(rawFile);
            setProfile((prev) => ({ ...prev, photo: previewURL }));
        }
    };

    const handleEditToggle = async () => {
        if (isEditing) {
            setSavingProfile(true);
            let finalProfileToSet = { ...editedProfile };
            let hasChanges = JSON.stringify(profile) !== JSON.stringify(editedProfile);

            if (imageChanged && newImageFile) {
                const formData = new FormData();
                formData.append("User[photo]", newImageFile);

                const imgRes = await MakeApiCall(
                    `user/${Id}/update-image`,
                    "post",
                    formData,
                    true,
                    { "Content-Type": undefined },
                    userData?.token
                );

                if (imgRes?.status) {
                    message.success("Profile picture updated!");
                    finalProfileToSet.photo = imgRes?.data?.photo;
                    hasChanges = true;
                } else {
                    message.error("Image update failed.");
                    setSavingProfile(false);
                    return;
                }
            }

            if (hasChanges) {
                const { photo, ...payloadWithoutPhoto } = finalProfileToSet;
                const response = await MakeApiCall(`user/${Id}`, "put", payloadWithoutPhoto, true, { "Content-Type": "application/json" }, userData?.token);

                if (response?.status) {
                    message.success("Profile updated successfully!");
                    setProfile(finalProfileToSet);
                    if (typeof setData === "function") {
                        setData({ ...userData, user: { ...userData.user, user_data: { ...userData.user.user_data, ...finalProfileToSet } } });
                    }
                }
            }
            setImageChanged(false);
            setNewImageFile(null);
            setSavingProfile(false);
        } else {
            setEditedProfile({ ...profile });
        }
        setIsEditing(!isEditing);
    };

    const handleCancelEdit = () => {
        setEditedProfile(null);
        setImageChanged(false);
        setNewImageFile(null);
        setIsEditing(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedProfile((prev) => ({ ...prev, [name]: value }));
    };

    // --- Password Handlers ---
    const handlePasswordUpdate = async () => {
        const { old_password, new_password, confirm_password } = passwordData;
        if (!old_password || !new_password || !confirm_password) return message.error("Fill all fields");
        if (new_password !== confirm_password) return message.error("Passwords mismatch");

        setLoadingPassword(true);
        const response = await MakeApiCall(`user/${Id}/change-password`, "put", { old_password, new_password }, true, { "Content-Type": "application/json" }, userData?.token);
        setLoadingPassword(false);
        if (response?.status) {
            message.success("Password updated!");
            setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
            setIsChangingPassword(false);
        }
    };

    if (!profile) return null;

    return (
        <Wrapper>
            <Helmet><title>Profile</title></Helmet>

            <div className="row gy-5 g-xl-5 mt-2">
                {/* LEFT PANEL */}
                <div className="col-xl-4 d-flex">
                    <div className="card card-flush h-100 w-100 mb-5 mb-xl-8">
                        <div className="card-header border-bottom-dashed">
                            <h3 className="card-title fw-bolder">Account</h3>
                        </div>

                        <div className="card-body d-flex flex-column align-items-center text-center py-8">
                            <Upload
                                showUploadList={false}
                                beforeUpload={beforeUpload}
                                onChange={handleProfilePictureChange}
                                disabled={!isEditing || !canEdit}
                            >
                                <div className={`profile-avatar-wrapper mb-5 ${isEditing ? "is-editing" : ""}`}>
                                    <img
                                        src={profile?.photo || "https://via.placeholder.com/120"}
                                        className="w-100 h-100 object-fit-cover"
                                        alt="avatar"
                                    />
                                    {isEditing && (
                                        <div className="avatar-overlay">
                                            <i className="bi bi-camera-fill text-white fs-2"></i>
                                        </div>
                                    )}
                                </div>
                            </Upload>

                            <h3 className="fw-bolder text-gray-800 fs-3 mb-1">{profile.name}</h3>
                            <span className="text-gray-500 fw-semibold fs-7 mb-6">
                                {profile?.role_name || profile?.designation || "Member"}
                            </span>

                            <div className="w-100 mb-6">
                                <div className="d-flex align-items-center bg-light rounded p-3 mb-3">
                                    <span className="symbol symbol-35px me-3">
                                        <span className="symbol-label bg-light-danger">
                                            <i className="bi bi-envelope-fill text-danger fs-5"></i>
                                        </span>
                                    </span>
                                    <div className="d-flex flex-column text-start overflow-hidden">
                                        <span className="text-gray-500 fs-8 fw-semibold">Email</span>
                                        <span className="text-gray-800 fw-bold fs-7 text-truncate">{profile.email}</span>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center bg-light rounded p-3">
                                    <span className="symbol symbol-35px me-3">
                                        <span className="symbol-label bg-light-danger">
                                            <i className="bi bi-telephone-fill text-danger fs-5"></i>
                                        </span>
                                    </span>
                                    <div className="d-flex flex-column text-start">
                                        <span className="text-gray-500 fs-8 fw-semibold">Phone</span>
                                        <span className="text-gray-800 fw-bold fs-7">{profile.contact_no || "N/A"}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                className={`btn btn-sm w-100 fw-bold ${isChangingPassword ? "btn-light" : "btn-light-danger"}`}
                                onClick={() => { setIsChangingPassword(!isChangingPassword); setIsEditing(false); }}
                                disabled={!canEdit}
                            >
                                <i className={`bi ${isChangingPassword ? "bi-arrow-left" : "bi-shield-lock-fill"} me-2`}></i>
                                {isChangingPassword ? "Back to Profile" : "Change Password"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL */}
                <div className="col-xl-8 d-flex">
                    <div className="card card-flush h-100 w-100 mb-5 mb-xl-8">
                        <div className="card-header border-bottom-dashed">
                            <h3 className="card-title align-items-start flex-column">
                                <span className="card-label fw-bolder fs-3">
                                    {isChangingPassword ? "Security Settings" : "Personal Information"}
                                </span>
                                <span className="text-muted mt-1 fw-semibold fs-7">
                                    {isChangingPassword ? "Update your account password" : "Edit your basic account details"}
                                </span>
                            </h3>
                            {!isChangingPassword && (
                                <div className="card-toolbar">
                                    {isEditing ? (
                                        <>
                                            <button
                                                className="btn btn-sm btn-light fw-bold me-2"
                                                onClick={handleCancelEdit}
                                                disabled={savingProfile || !canEdit}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn btn-sm btn-danger fw-bold"
                                                onClick={handleEditToggle}
                                                disabled={savingProfile || !canEdit}
                                            >
                                                {savingProfile ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-check2 me-1"></i>
                                                        Save Changes
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            className="btn btn-sm btn-light-danger fw-bold"
                                            onClick={handleEditToggle}
                                            disabled={!canEdit}
                                        >
                                            <i className="bi bi-pencil-square me-1"></i>
                                            Edit Profile
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="card-body py-6">
                            {isChangingPassword ? (
                                <div>
                                    <PasswordRow
                                        label="Current Password"
                                        name="old_password"
                                        value={passwordData.old_password}
                                        onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                                    />
                                    <PasswordRow
                                        label="New Password"
                                        name="new_password"
                                        value={passwordData.new_password}
                                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                    />
                                    <PasswordRow
                                        label="Confirm Password"
                                        name="confirm_password"
                                        value={passwordData.confirm_password}
                                        onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                                    />
                                    <div className="d-flex justify-content-end pt-3">
                                        <Button
                                            type="primary"
                                            danger
                                            size="large"
                                            loading={loadingPassword}
                                            onClick={handlePasswordUpdate}
                                            disabled={!canEdit}
                                            className="fw-bold"
                                        >
                                            Update Password
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <FormRow
                                        label="Full Name"
                                        name="name"
                                        icon="bi-person-fill"
                                        value={isEditing ? editedProfile?.name : profile?.name}
                                        isEditing={isEditing}
                                        onChange={handleChange}
                                    />
                                    <FormRow
                                        label="Email"
                                        name="email"
                                        icon="bi-envelope-fill"
                                        value={profile?.email}
                                        isEditing={false}
                                        readOnlyHint="Email cannot be changed"
                                    />
                                    <FormRow
                                        label="Contact"
                                        name="contact_no"
                                        icon="bi-telephone-fill"
                                        value={isEditing ? editedProfile?.contact_no : profile?.contact_no}
                                        isEditing={isEditing}
                                        onChange={handleChange}
                                    />
                                    <FormRow
                                        label="Address"
                                        name="address"
                                        icon="bi-geo-alt-fill"
                                        value={isEditing ? editedProfile?.address : profile?.address}
                                        isEditing={isEditing}
                                        onChange={handleChange}
                                    />
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};

const FormRow = ({ label, name, value, isEditing, onChange, icon, readOnlyHint }) => (
    <div className="row mb-7 align-items-center">
        <label className="col-lg-4 col-form-label fw-semibold fs-6 text-gray-600">
            {label}
            {readOnlyHint && (
                <i
                    className="bi bi-info-circle ms-2 text-muted"
                    title={readOnlyHint}
                    style={{ fontSize: "12px" }}
                ></i>
            )}
        </label>
        <div className="col-lg-8">
            {isEditing ? (
                <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                        <i className={`bi ${icon} text-gray-600`}></i>
                    </span>
                    <input
                        type="text"
                        name={name}
                        value={value || ""}
                        onChange={onChange}
                        className="form-control form-control-solid border-start-0"
                    />
                </div>
            ) : (
                <span className="fw-bold fs-6 text-gray-800 d-block py-2">
                    {value || <span className="text-muted fw-normal">—</span>}
                </span>
            )}
        </div>
    </div>
);

const PasswordRow = ({ label, name, value, onChange }) => (
    <div className="row mb-7 align-items-center">
        <label className="col-lg-4 col-form-label fw-semibold fs-6 text-gray-600">{label}</label>
        <div className="col-lg-8">
            <Input.Password name={name} size="large" value={value} onChange={onChange} />
        </div>
    </div>
);

export default MyProfile;
